/**
 * Quality Command Center — shared presentational primitives.
 * Result classes render as text + icon (never colour-alone) with accessible names.
 */
import {
  CheckCircle2, XCircle, Wrench, Ban, MinusCircle, HelpCircle, AlertTriangle,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { type QaResult } from '@/lib/qa/commandCenterLogic';

export const RESULT_META: Record<QaResult, { label: string; icon: React.ComponentType<{ className?: string }>; cls: string }> = {
  PASS: { label: 'Pass', icon: CheckCircle2, cls: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
  FAIL_PRODUCT: { label: 'Fail — Product Defect', icon: XCircle, cls: 'border-red-300 text-red-700 bg-red-50' },
  FAIL_HARNESS: { label: 'Fail — QA Harness Defect', icon: Wrench, cls: 'border-orange-300 text-orange-700 bg-orange-50' },
  BLOCKED_ENV: { label: 'Blocked — Environment', icon: Ban, cls: 'border-amber-300 text-amber-700 bg-amber-50' },
  BLOCKED_PRODUCT: { label: 'Blocked — Product Defect', icon: Ban, cls: 'border-amber-400 text-amber-800 bg-amber-100' },
  NOT_EXECUTED: { label: 'Not Executed', icon: MinusCircle, cls: 'border-slate-300 text-slate-600 bg-slate-50' },
  UNKNOWN: { label: 'Unknown', icon: HelpCircle, cls: 'border-slate-400 text-slate-700 bg-slate-100' },
  ARTIFACT_ERROR: { label: 'Artifact Error', icon: AlertTriangle, cls: 'border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50' },
};

export function ResultBadge({ result, compact = false }: { result: QaResult; compact?: boolean }) {
  const m = RESULT_META[result] ?? RESULT_META.UNKNOWN;
  const Icon = m.icon;
  return (
    <span role="status" aria-label={`Result: ${m.label}`} className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden /> {compact ? '' : m.label}
    </span>
  );
}

export function Loading() {
  return <div className="py-12 text-center text-sm text-muted-foreground" role="status" aria-live="polite">Loading evidence…</div>;
}

export function ErrorState({ e }: { e: unknown }) {
  return (
    <Alert variant="destructive" className="my-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Evidence unavailable</AlertTitle>
      <AlertDescription>Could not load QA evidence ({String((e as Error)?.message ?? e)}). This is shown as an error — it is NOT treated as a pass.</AlertDescription>
    </Alert>
  );
}

export function Empty({ what }: { what: string }) {
  return <div className="py-8 text-center text-sm text-muted-foreground">No {what} evidence ingested. This is <strong>NOT_EXECUTED</strong>, not a pass.</div>;
}

export function Insufficient({ what }: { what: string }) {
  return <div className="py-6 text-center text-sm text-muted-foreground border rounded-md bg-muted/20">Insufficient executed evidence{what ? ` for ${what}` : ''}.</div>;
}

export function ageLabel(iso?: string | null): { text: string; stale: boolean } {
  if (!iso) return { text: 'unknown', stale: true };
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { text: 'unknown', stale: true };
  const days = (Date.now() - then) / 86_400_000;
  if (days < 1) return { text: 'today', stale: false };
  return { text: `${Math.round(days)}d ago`, stale: days >= 7 };
}

export function fmtDuration(ms?: number | null): string {
  if (ms == null) return 'not captured';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

/** A tiny inline SVG sparkline with an accessible text summary — no chart library, CSP-safe. */
export function Sparkline({ values, label, width = 220, height = 40 }: { values: number[]; label: string; width?: number; height?: number }) {
  if (!values.length) return <span className="text-xs text-muted-foreground">no data</span>;
  const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1 || 1)) * width},${height - ((v - min) / span) * height}`).join(' ');
  const summary = `${label}: ${values.length} points, min ${min}, max ${max}, last ${values[values.length - 1]}`;
  return (
    <svg width={width} height={height} role="img" aria-label={summary} className="overflow-visible">
      <title>{summary}</title>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" />
    </svg>
  );
}
